/** It manages the input value, controlling the format as the user types, deletions, and ensuring valid date parts.
 * The returned value is always formatted as "MM/DD/YYYY" allowing partial inputs.
 * When deleting characters, it preserves the user's input without reformatting.
 * It allows years with 4 digits, and months and days with 1 or 2 digits.
 * It restricts invalid values for month, day, and year as the user types.
 * It does not allow zero values for month or day.
 * It ensures proper placement of slashes.
 * Examples of accepted inputs: "2", "02", "2/3", "02/03", "2/3/2020", "02/03/2020".
 */
export function handleDateInputChange(rawValue: string, prevValue: string): string {
  const isDeleting = rawValue.length < prevValue.length;
  if (isDeleting) return rawValue;

  let value = sanitizeInput(rawValue);
  let { month, day, year } = splitDateParts(value);

  month = formatMonth(month);
  const numMonth = parseInt(month, 10) || undefined;
  day = formatDay(day, numMonth);
  year = formatYear(year);

  const formatted = buildFormattedDate(value, month, day, year);
  return formatted;
}

/**
 * Evaluates whether a given date string represents a valid calendar date.
 *
 * The input is expected to contain separate string parts for month, day, and year.
 * It checks for completeness, numeric validity, logical ranges (e.g., month between 1–12),
 * and whether the day value fits within the correct number of days for the specified month and year.
 *
 * Returns an object indicating whether the date is valid (`isValid`), and if not,
 * a `reason` describing the failure:
 *
 * - `"incomplete"` – One or more parts of the date are missing or the year is not 4 digits.
 * - `"non-valid-values"` – Parts of the date are non-numeric or outside valid ranges.
 * - `"non-leap-year"` – The day exceeds the valid range for February in a non–leap year.
 *
 * Examples of valid dates: "02/29/2020", "2/9/2020", "12/31/1999".
 * Examples of invalid dates: "00/10/2020", "13/01/2020", "02/30/2021", "04/31/2020", "2/29/19".
 *
 * @param value - The date string to evaluate.
 * @returns An object with `{ isValid: boolean, reason?: string }`.
 */

export function evaluateDate(value: string): { isValid: boolean; reason?: string } {
  let { month: monthStr, day: dayStr, year: yearStr } = splitDateParts(value);
  if (!monthStr || !dayStr || !yearStr || yearStr.length !== 4)
    return { isValid: false, reason: "incomplete" };

  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);
  let year = parseInt(yearStr, 10);

  if (isNaN(month) || isNaN(day) || isNaN(year))
    return { isValid: false, reason: "non-valid-values" };
  if (month < 1 || month > 12 || day < 1 || year < 0 || year > 9999)
    return { isValid: false, reason: "non-valid-values" };

  const maxDays = daysInMonth(month, year);
  if (day > maxDays) {
    if (maxDays === 28) return { isValid: false, reason: "non-leap-year" };
    return { isValid: false, reason: "non-valid-values" };
  }

  return { isValid: true };
}

/** Error messages for InputField when the user types a non valid value
 * according to some different cases.
 * incomplete: When a date has less digits than needed.
 * non-valid-values: When a date has values that are not valid (ex: day 32 or month -1)
 * non-leap-year: When a date is 02/29 but it belongs to a non-leap year
 * max-limit: When a date exceeds the allowed range
 * min-limit: When a date is before the allowed range
 * unknown: Default error message
 */
export type ErrorReason =
  | "incomplete"
  | "non-valid-values"
  | "non-leap-year"
  | "max-limit"
  | "min-limit"
  | "unknown";
export const ERROR_MESSAGES: Record<ErrorReason, string> = {
  incomplete: "Incomplete date (Use MM/DD/YYYY)",
  "non-valid-values": "Invalid date",
  "non-leap-year": "Non leap year",
  "max-limit": "Date exceeds the allowed range",
  "min-limit": "Date is before the allowed range",
  unknown: "Enter a valid date.",
};

/* -------------------------------------------------------------------------- */
/* ------------------------------- AUX FUNCTIONS ---------------------------- */
/* -------------------------------------------------------------------------- */

/** It just allow numbers and slashes "/" */
function sanitizeInput(value: string): string {
  // trim leading/trailing spaces
  value = value.trim();
  // allow only digits and slashes
  value = value.replace(/[^\d/]/g, "");
  // collapse multiple slashes
  value = value.replace(/\/+/g, "/");
  // remove leading slash
  value = value.replace(/^\/+/g, "");
  return value;
}

export function sanitizeMobile(value: string): string {
  // trim leading/trailing spaces
  value = value.trim();
  // allow only digits
  value = value.replace(/[^\d]/g, "");
  return value;
}

/** Divides the date into its parts using the slash as a separator.
 * It ensures there are always three parts and fills missing parts with empty strings.
 */
function splitDateParts(value: string): { month: string; day: string; year: string } {
  const parts = value.split("/").slice(0, 3);
  while (parts.length < 3) parts.push("");
  return { month: parts[0], day: parts[1], year: parts[2] };
}

/** Formats the month according to the defined rules.
 * When the month has 1 digit greater than 2, it is interpreted as "0X".
 * If the month exceeds 12, it is corrected to "01".
 */
export function formatMonth(month: string): string {
  if (!month) return "";

  if (month.length === 1) {
    const num = parseInt(month, 10);
    if (num >= 2) {
      return `0${num}`;
    }
    //Wait for the second digit
    return month;
  }
  if (month.length == 2) {
    const num = parseInt(month, 10);
    if (num === 0) return "01";
    if (num > 12) {
      return `${month.charAt(0)}`;
    }
  }
  if (month.length > 2) {
    if (parseInt(month.slice(0, 2)) > 12) return "01";
  }

  // Limit to 2 digits
  return month.slice(0, 2);
}

/** Returns max days allowed for the given month.
 * Be permissive for February (allow 29) if year is not provided.
 */
function daysInMonth(month?: number, year?: number): number {
  if (!month || month < 1 || month > 12) return 31;
  if (month === 2) return year ? (isLeapYear(year) ? 29 : 28) : 29;
  if ([4, 6, 9, 11].includes(month)) return 30;
  return 31;
}

/** Formats the day according to the defined rules.
 * When the day has 1 digit greater than 3, it is interpreted as "0X".
 * If the day exceeds the maximum allowed for the month, it is corrected accordingly.
 */
export function formatDay(day: string, month?: number): string {
  if (!day) return "";
  const maxDays = daysInMonth(month);

  if (day.length === 1) {
    const num = parseInt(day, 10);
    if ((maxDays === 29 && num > 2) || (maxDays > 29 && num > 3)) {
      return `0${num}`;
    }
    //Wait for the second digit
    return day;
  }
  if (day.length === 2) {
    const num = parseInt(day, 10);
    if (num === 0) return "01";
    if (num > maxDays) {
      return `${day.charAt(0)}`;
    }
  }
  if (day.length > 2) {
    if (parseInt(day.slice(0, 2)) > maxDays) return "01";
  }

  // Limit to 2 digits
  return day.slice(0, 2);
}

/** Limits the year to four digits*/
export function formatYear(year: string): string {
  return year ? year.slice(0, 4) : "";
}

/** Indicates if the provided year is a leap year. It means February has 29 days. */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** Builds the formatted date string "MM/DD/YYYY" based on the provided parts and the raw input value.
 * It ensures proper placement of slashes. */
function buildFormattedDate(rawValue: string, month: string, day: string, year: string): string {
  let formatted = month;

  // Insert slash after month if needed
  const hasMonthSlash = rawValue.charAt(2) === "/" || rawValue.charAt(1) === "/";
  if ((month.length === 2 && !hasMonthSlash) || hasMonthSlash) formatted += "/";

  // Insert slash after day if needed
  if (day) formatted += day.length === 2 || rawValue.length > 4 ? `${day}/` : day;

  // Append year
  if (year) formatted += year;

  // Normalize cases like "3/" -> "03/"
  if (rawValue.endsWith("/")) {
    if (!day) formatted = `${month.padStart(2, "0")}/`;
    else if (!year) formatted = `${month}/${day.padStart(2, "0")}/`;
  }
  return formatted;
}
